import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useColorPalette } from '../hooks/useColorPalette';

/**
 * Componente per le icone degli step del wizard
 */
const StepIcon = ({ icon, isActive, isCompleted, colors }) => {  
    return (
        <div 
            style={{
                width: '2.5rem',
                height: '2.5rem',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: isActive ? colors.primary : '#ffffff',
                color: isActive ? '#ffffff' : '#555555',
                border: `1px solid ${isActive ? colors.primary : '#dddddd'}`,
            }}
        >
            <FontAwesomeIcon icon={icon} />
        </div>
    );
};

/**
 * Header per i wizard con step multipli e indicatori di progresso
 * @param {Object} props - Props del componente
 * @param {Array} props.steps - Array di step con { title, icon }
 * @param {number} props.currentStep - Step corrente
 * @param {Function} props.setCurrentStep - Funzione per cambiare lo step corrente
 * @param {boolean} props.zeroBasedIndex - Se true, currentStep è zero-based (0,1,2...), altrimenti è one-based (1,2,3...)
 * @param {boolean} props.showConnectingLines - Se true, mostra le linee di connessione tra le icone
 * @param {Object} props.customStyles - Stili personalizzati (opzionale)
 */
const WizardHeader = ({ steps, currentStep, setCurrentStep, zeroBasedIndex = false, showConnectingLines = false, customStyles = {} }) => {
    const colors = useColorPalette();
    
    // Normalizza il currentStep per le operazioni interne
    const normalizedStep = zeroBasedIndex ? currentStep + 1 : currentStep;

    return (
        <div className="bg-transparent border-0 p-0">
            <div className="bg-transparent">
                <div className="position-relative mb-4" style={{ marginTop: '20px', ...customStyles.container }}>
                    {/* Container per gli step distribuiti equamente */}
                    <div className="d-flex justify-content-between px-5 position-relative" style={{ zIndex: 2 }}>
                        {/* Linee orizzontali - una per ogni step tranne l'ultimo */}
                        {showConnectingLines && steps.map((_, index) => {
                            // Saltiamo l'ultimo step, che non ha linea a destra
                            if (index === steps.length - 1) return null;
                            
                            const stepNumber = index + 1;
                            const spaceBetween = 100 / steps.length; // Percentuale di spazio tra le icone
                            
                            return (
                                <div 
                                    key={`line-${stepNumber}`}
                                    className="position-absolute" 
                                    style={{
                                        position: 'absolute',
                                        height: '2px',
                                        backgroundColor: colors.primary,
                                        width: `calc(${spaceBetween}% - 3.5rem)`, // Larghezza tra le icone
                                        top: '1.25rem',
                                        left: `calc(${index * spaceBetween}% + 3.5rem)`, // Posizionata dopo l'icona
                                        zIndex: 1,
                                        opacity: normalizedStep > stepNumber ? 1 : 0,
                                        transition: 'opacity 0.3s ease',
                                        ...customStyles.progressLine
                                    }}
                                ></div>
                            );
                        })}
                        
                        {/* Rendering di tutti gli step */}
                        {steps.map((step, index) => {
                            const stepNumber = index + 1;
                            return (
                                <div 
                                    key={`step-${stepNumber}`}
                                    className="d-flex flex-column align-items-center" 
                                    style={{ 
                                        width: '120px',
                                        cursor: normalizedStep > stepNumber ? 'pointer' : 'default',
                                        ...customStyles.stepContainer
                                    }}
                                    onClick={() => {
                                        // Navigazione consentita solo agli step già completati
                                        if (normalizedStep > stepNumber) {
                                            // Converti indietro in base al tipo di indice usato
                                            const targetStep = zeroBasedIndex ? stepNumber - 1 : stepNumber;
                                            setCurrentStep(targetStep);
                                        }
                                    }}
                                >
                                    <StepIcon 
                                        icon={step.icon}
                                        isActive={normalizedStep >= stepNumber}
                                        isCompleted={normalizedStep > stepNumber}
                                        colors={colors}
                                    />
                                    <div 
                                        style={{
                                            color: '#6c757d',
                                            fontWeight: '500',
                                            fontSize: '0.75rem',
                                            marginTop: '8px',
                                            textTransform: 'uppercase',
                                            ...customStyles.stepLabel
                                        }}
                                    >
                                        {step.title}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default WizardHeader;
